<template>
  <div class="dashboard-container container">
    <!-- Header -->
    <div class="dashboard-header">
      <h1>{{ $t('dashboard') }}</h1>
      <p>{{ $t('welcomeBack') }}, {{ userType }}</p>
    </div>

    <!-- Quick Stats -->
    <div class="stats-grid">
      <el-card shadow="hover" class="stat-card" v-if="showUsersCard">
        <el-icon :size="40" color="#409EFC"><User /></el-icon>
        <div class="stat-info">
          <div class="stat-value">{{ usersCount }}</div>
          <div class="stat-title">{{ $t('users') }}</div>
        </div>
      </el-card>
      <el-card shadow="hover" class="stat-card" v-if="showEmployeesCard">
        <el-icon :size="40" color="#67C23A"><OfficeBuilding /></el-icon>
        <div class="stat-info">
          <div class="stat-value">{{ employeesCount }}</div>
          <div class="stat-title">{{ $t('employees') }}</div>
        </div>
      </el-card>
      <el-card shadow="hover" class="stat-card" v-if="showTreasuryCard">
        <el-icon :size="40" color="#E6A23C"><Wallet /></el-icon>
        <div class="stat-info">
          <div class="stat-value">{{ treasuryBalance }}</div>
          <div class="stat-title">{{ $t('treasury') }}</div>
        </div>
      </el-card>
      <el-card shadow="hover" class="stat-card" v-if="showStudentsCard">
        <el-icon :size="40" color="#F56C6C"><School /></el-icon>
        <div class="stat-info">
          <div class="stat-value">{{ studentsCount }}</div>
          <div class="stat-title">{{ $t('students') }}</div>
        </div>
      </el-card>
    </div>

    <!-- Quick Access Sections -->
    <div class="quick-access-grid">
      <el-card shadow="hover" v-if="showStudentManagementSection">
        <template #header>
          <div class="card-header">
            <el-icon><User /></el-icon>
            <span>شؤون الطلاب </span>
          </div>
        </template>
        <div class="quick-links">
          <router-link v-for="item in studentAndHrItems" :key="item.href" :to="item.href" class="quick-link">
            <el-icon><component :is="item.icon" /></el-icon>
            <span>{{ item.text }}</span>
          </router-link>
        </div>
      </el-card>

      <el-card shadow="hover" v-if="showAcademicSection">
        <template #header>
          <div class="card-header">
            <el-icon><Reading /></el-icon>
            <span>الإدارة الأكاديمية</span>
          </div>
        </template>
        <div class="quick-links">
          <router-link v-for="item in academicManagementItems" :key="item.href" :to="item.href" class="quick-link">
            <el-icon><component :is="item.icon" /></el-icon>
            <span>{{ item.text }}</span>
          </router-link>
        </div>
      </el-card>

      <el-card shadow="hover" v-if="showFinancialSection">
        <template #header>
          <div class="card-header">
            <el-icon><Money /></el-icon>
            <span>الإدارة المالية</span>
          </div>
        </template>
        <div class="quick-links">
          <router-link v-for="item in financialManagementItems" :key="item.href" :to="item.href" class="quick-link">
            <el-icon><component :is="item.icon" /></el-icon>
            <span>{{ item.text }}</span>
          </router-link>
        </div>
      </el-card>
      
      <el-card shadow="hover" v-if="showSystemSettingsSection">
        <template #header>
          <div class="card-header">
            <el-icon><Setting /></el-icon>
            <span>إعدادات النظام</span>
          </div>
        </template>
        <div class="quick-links">
          <router-link v-for="item in systemSettingsItems" :key="item.href" :to="item.href" class="quick-link">
            <el-icon><component :is="item.icon" /></el-icon>
            <span>{{ item.text }}</span>
          </router-link>
        </div>
      </el-card>

      <el-card shadow="hover" v-if="showHealthKitchenSection">
        <template #header>
          <div class="card-header">
            <el-icon><Setting /></el-icon>
            <span>الصحة والمطبخ</span>
          </div>
        </template>
        <div class="quick-links">
          <router-link v-for="item in healthKitchenItems" :key="item.href" :to="item.href" class="quick-link">
            <el-icon><component :is="item.icon" /></el-icon>
            <span>{{ item.text }}</span>
          </router-link>
        </div>
      </el-card>

      <el-card shadow="hover" v-if="showAccountManagementSection">
        <template #header>
          <div class="card-header">
            <el-icon><User /></el-icon>
            <span>إدارة الحسابات</span>
          </div>
        </template>
        <div class="quick-links">
          <router-link v-for="item in accountManagementItems" :key="item.href" :to="item.href" class="quick-link">
            <el-icon><component :is="item.icon" /></el-icon>
            <span>{{ item.text }}</span>
          </router-link>
        </div>
      </el-card>

    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from 'axios';
import { User, OfficeBuilding, Wallet, School, Reading, Money, Setting, Collection, Files, Calendar, ChatDotRound, Bell, Tickets, Briefcase, TrendCharts, DataAnalysis, Umbrella, Edit, PriceTag, SetUp, Help, Van, Utensils } from '@element-plus/icons-vue';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export default {
  name: "Dashboard",
  components: { User, OfficeBuilding, Wallet, School, Reading, Money, Setting, Collection, Files, Calendar, ChatDotRound, Bell, Tickets, Briefcase, TrendCharts, DataAnalysis, Umbrella, Edit, PriceTag, SetUp, Help, Van, Utensils },
  data() {
    return {
      usersCount: 0,
      employeesCount: 0,
      treasuryBalance: 0,
      studentsCount: 0,
    };
  },
  computed: {
    ...mapGetters(['isDarkMode', 'userType','username']),
    // Visibility controls for stat cards
    showUsersCard() { 
      return ['moder', 'developer'].includes(this.userType); 
    },
    showEmployeesCard() { 
      return ['moder', 'financial', 'developer'].includes(this.userType); 
    },
    showTreasuryCard() { 
      return ['moder', 'developer'].includes(this.userType); 
    },
    showStudentsCard() { 
      return ['moder', 'financial', 'student_m', 'hr', 'student supervisor', 'developer'].includes(this.userType); 
    },
    
    // Visibility controls for quick access sections
    showStudentManagementSection() { 
      return ['moder', 'student_m', 'hr', 'cashier', 'student supervisor', 'developer'].includes(this.userType); 
    },
    showAcademicSection() { 
      return ['moder', 'developer'].includes(this.userType); 
    },
    showFinancialSection() { 
      // Moder should always see financial section unless username is academic_cis
      if (this.userType === 'moder') {
        return this.username !== 'academic_cis';
      }
      const allowedRoles = ['moder', 'financial', 'cashier', 'developer'];
      return allowedRoles.includes(this.userType) && this.username !== 'academic_cis'; 
    },
    showSystemSettingsSection() { 
      return ['moder', 'developer'].includes(this.userType); 
    },
    showHealthKitchenSection() { 
      return ['moder', 'doctor', 'financial', 'hr', 'cashier', 'student supervisor', 'developer'].includes(this.userType); 
    },
    showAccountManagementSection() { 
      return ['moder', 'hr', 'prin', 'student_m', 'developer', 'teacher'].includes(this.userType); 
    },
    
    // Quick access items - filtered by permissions (Essential items only)
    studentAndHrItems() {
      const allItems = [
        { icon: 'User', text: this.$t('studentsManagement'), href: '/students', roles: ['hr', 'prin', 'moder', 'student_m', 'developer'] },
        { icon: 'Umbrella', text: this.$t('parentsManagement'), href: '/parents', roles: ['admin', 'student_m', 'prin', 'moder', 'developer'] },
        { icon: 'User', text: this.$t('parentsAccounts'), href: '/parents-account', roles: ['hr', 'prin', 'moder', 'student_m', 'developer', 'teacher'] },
        { icon: 'User', text: this.$t('teachersAccounts'), href: '/teachers-account', roles: ['hr', 'prin', 'moder', 'student_m', 'developer', 'teacher'] },
        { icon: 'OfficeBuilding', text: this.$t('employeesManagement'), href: '/employees', roles: ['admin', 'hr', 'moder', 'developer'] },
        { icon: 'User', text: this.$t('studentIdCards'), href: '/student-id-cards', roles: ['hr', 'prin', 'moder', 'financial', 'developer'] },
        { icon: 'User', text: this.$t('studentsCards'), href: '/cards-students', roles: ['hr', 'student_m', 'moder', 'developer'] },
      ];
      
      // Filter items based on user permissions
      const hasPermission = (allowedRoles) => {
        if (!allowedRoles || allowedRoles.length === 0) return true;
        return allowedRoles.includes(this.userType);
      };
      return allItems.filter(item => hasPermission(item.roles));
    },
    // Account management items (shown in main dashboard)
    accountManagementItems() {
      const allItems = [
        { icon: 'User', text: this.$t('studentsManagement'), href: '/students', roles: ['hr', 'prin', 'moder', 'student_m', 'developer'] },
        { icon: 'Umbrella', text: this.$t('parentsManagement'), href: '/parents', roles: ['admin', 'student_m', 'prin', 'moder', 'developer'] },
        { icon: 'User', text: this.$t('parentsAccounts'), href: '/parents-account', roles: ['hr', 'prin', 'moder', 'student_m', 'developer', 'teacher'] },
        { icon: 'User', text: this.$t('teachersAccounts'), href: '/teachers-account', roles: ['hr', 'prin', 'moder', 'student_m', 'developer', 'teacher'] },
      ];
      
      // Filter items based on user permissions
      const hasPermission = (allowedRoles) => {
        if (!allowedRoles || allowedRoles.length === 0) return true;
        return allowedRoles.includes(this.userType);
      };
      return allItems.filter(item => hasPermission(item.roles));
    },
    academicManagementItems() {
        const allItems = [
            { icon: 'Collection', text: this.$t('subjects'), href: '/subjects', roles: ['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer', 'teacher'] },
            { icon: 'Files', text: this.$t('term'), href: '/terms', roles: ['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer'] },
            { icon: 'Files', text: this.$t('section'), href: '/classes-sections', roles: ['hr', 'prin', 'moder', 'student_m', 'developer'] },
            { icon: 'Files', text: this.$t('attendance'), href: '/attendance', roles: ['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'teacher', 'developer'] },
            { icon: 'Files', text: this.$t('exams'), href: '/exams', roles: ['hr', 'prin', 'moder', 'student_m', 'developer'] },
            { icon: 'Files', text: this.$t('academicSchedule'), href: '/academic-schedule', roles: ['hr', 'prin', 'moder', 'admin', 'student_m', 'financial', 'developer'] },
            { icon: 'School', text: this.$t('groups'), href: '/generate-groups', roles: ['hr', 'prin', 'moder', 'financial', 'developer', 'teacher'] },
            { icon: 'Briefcase', text: this.$t('assignSubjects'), href: '/assign-subjects', roles: ['hr', 'prin', 'moder', 'financial', 'developer'] },
            { icon: 'Briefcase', text: this.$t('teacherAssignment'), href: '/teacher-assignment', roles: ['hr', 'prin', 'moder', 'student_m', 'developer', 'teacher'] },
            { icon: 'Files', text: this.$t('curriculumSetup'), href: '/curriculum-setup', roles: ['hr', 'prin', 'moder', 'student_m', 'developer'] },
            { icon: 'School', text: this.$t('gradeLevel'), href: '/grade-levels', roles: ['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer'] },
        ];
        
        // Moder should see all items
        if (this.userType === 'moder') {
            return allItems;
        }
        
        // Filter items based on user permissions
        const hasPermission = (allowedRoles) => {
          if (!allowedRoles || allowedRoles.length === 0) return true;
          return allowedRoles.includes(this.userType);
        };
        return allItems.filter(item => hasPermission(item.roles));
    },
    financialManagementItems() {
      const allItems = [
        { icon: 'Tickets', text: this.$t('invoiceManagement'), href: '/invoice-management', roles: ['financial', 'hr', 'moder', 'developer'] },
        { icon: 'Files', text: this.$t('invoiceList'), href: '/invoice-list', roles: ['financial', 'hr', 'moder', 'developer'] },
        { icon: 'Wallet', text: this.$t('treasury'), href: '/treasury', roles: ['financial', 'moder', 'developer'] },
        { icon: 'Money', text: this.$t('salaries'), href: '/salaries', roles: ['financial', 'moder', 'hr', 'developer'] },
        { icon: 'DataAnalysis', text: this.$t('financialReports'), href: '/financial-reports', roles: ['moder', 'hr', 'prin', 'financial', 'developer'] },
        { icon: 'DataAnalysis', text: this.$t('chartOfAccounts'), href: '/chart-of-accounts', roles: ['moder', 'hr', 'prin', 'financial', 'developer'] },
        { icon: 'Briefcase', text: this.$t('vendorManagement'), href: '/vendors', roles: ['moder', 'hr', 'prin', 'financial', 'developer'] },
        { icon: 'Collection', text: this.$t('billManagement'), href: '/bills', roles: ['moder', 'hr', 'prin', 'financial', 'developer'] },
        { icon: 'PriceTag', text: this.$t('feeTypeManagement'), href: '/fee-type-management', roles: ['hr', 'financial', 'moder', 'developer'] },
        { icon: 'SetUp', text: this.$t('feeStructureManagement'), href: '/fee-structure-management', roles: ['hr', 'financial', 'moder', 'developer'] },
      ];
      
      // Moder should see all items (unless username is academic_cis)
      if (this.userType === 'moder' && this.username !== 'academic_cis') {
        return allItems;
      }
      
      // Filter items based on user permissions for other users
      const hasPermission = (allowedRoles) => {
        if (!allowedRoles || allowedRoles.length === 0) return true;
        return allowedRoles.includes(this.userType);
      };
      return allItems.filter(item => hasPermission(item.roles));
    },
    systemSettingsItems() {
        const allItems = [
            { icon: 'Calendar', text: this.$t('studyYear'), href: '/study-years', roles: ['moder', 'hr', 'prin', 'admin', 'student_m', 'financial', 'developer'] },
            { icon: 'School', text: this.$t('classesManagement'), href: '/classes', roles: ['moder', 'student_m', 'hr', 'admin', 'developer'] },
            { icon: 'Setting', text: this.$t('departments'), href: '/departments', roles: ['hr', 'prin', 'moder', 'developer'] },
            { icon: 'Setting', text: this.$t('users'), href: '/users', roles: ['moder', 'developer'] },
            { icon: 'Setting', text: this.$t('registerUsers'), href: '/register-users', roles: ['hr', 'prin', 'moder', 'student_m', 'developer'] },
            { icon: 'Bell', text: this.$t('announcementSender'), href: '/announcement-sender', roles: ['hr', 'prin', 'moder', 'student_m', 'developer'] },
            { icon: 'Bell', text: this.$t('notificationSender'), href: '/notification-sender-parents', roles: ['hr', 'prin', 'moder', 'student_m', 'developer'] },
            { icon: 'Bell', text: this.$t('userNotification'), href: '/user-notification', roles: ['hr', 'prin', 'moder', 'student_m', 'developer'] },
        ];

        // Moder should see all items (unless username is academic_cis)
        if (this.userType === 'moder' && this.username !== 'academic_cis') {
            return allItems;
        }

        // Filter items based on user permissions for other users
        const hasPermission = (allowedRoles) => {
          if (!allowedRoles || allowedRoles.length === 0) return true;
          return allowedRoles.includes(this.userType);
        };
        return allItems.filter(item => hasPermission(item.roles));
    },
    healthKitchenItems() {
        const allItems = [
            { icon: 'Help', text: this.$t('healthFile'), href: '/health-files', roles: ['moder', 'doctor', 'developer'] },
            { icon: 'Utensils', text: this.$t('kitchen'), href: '/kitchen', roles: ['moder', 'financial', 'developer'] },
            { icon: 'Utensils', text: this.$t('kitchenSales'), href: '/kitchen-sales', roles: ['moder', 'cashier', 'developer'] },
        ];
        
        // Moder should see all items
        if (this.userType === 'moder') {
            return allItems;
        }
        
        // Filter items based on user permissions
        const hasPermission = (allowedRoles) => {
          if (!allowedRoles || allowedRoles.length === 0) return true;
          return allowedRoles.includes(this.userType);
        };
        let filteredItems = allItems.filter(item => hasPermission(item.roles));
        
        // For cashier, only show kitchen-sales
        if (this.userType === 'cashier') {
            filteredItems = filteredItems.filter(item => 
                ['/kitchen-sales'].includes(item.href)
            );
        }
        
        return filteredItems;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    // Helper function to check permissions
    hasPermission(allowedRoles) {
      if (!allowedRoles || allowedRoles.length === 0) return true;
      return allowedRoles.includes(this.userType);
    },
    async fetchData() {
      try {
        const [usersResponse, employeesResponse, treasuryResponse, studentsResponse] = await Promise.all([
          api.get('/statistics/users'),
          api.get('/statistics/employees'),
          api.get('/statistics/treasury/manual-balance'),
          api.get('/statistics/students'),
        ]);

        this.usersCount = usersResponse.data.total_users || 0;
        this.employeesCount = employeesResponse.data.total_employees || 0;
        this.treasuryBalance = treasuryResponse.data.manual_treasury_balance || 0;
        this.studentsCount = studentsResponse.data.total_students || 0;
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    },
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 32px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: calc(100vh - 120px);
  transition: all 0.3s ease;
}

.dashboard-container:deep(.dark-mode) {
  background: linear-gradient(135deg, #0a0e14 0%, #1a1f2e 100%);
}

.dashboard-header {
  margin-bottom: 32px;
  padding: 24px 0;
}

.dashboard-header h1 {
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
}

.dashboard-header p {
  font-size: 1.1rem;
  color: #6b7280;
  font-weight: 500;
}

.dashboard-container:deep(.dark-mode) .dashboard-header p {
  color: #9ca3af;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.stat-card:hover::before {
  transform: scaleX(1);
}

.dashboard-container:deep(.dark-mode) .stat-card {
  background: linear-gradient(135deg, #1a1f2e 0%, #0f1419 100%);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  color: #1f2937;
  line-height: 1.2;
  margin-bottom: 4px;
}

.dashboard-container:deep(.dark-mode) .stat-value {
  color: #ffffff;
}

.stat-title {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dashboard-container:deep(.dark-mode) .stat-title {
  color: #9ca3af;
}

.quick-access-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 24px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 700;
  font-size: 1.2rem;
  color: #1f2937;
  padding: 4px 0;
}

.dashboard-container:deep(.dark-mode) .card-header {
  color: #ffffff;
}

.quick-links {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 8px 0;
}

.quick-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  text-decoration: none;
  color: #374151;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(102, 126, 234, 0.05);
  border: 1px solid rgba(102, 126, 234, 0.1);
  font-weight: 500;
  position: relative;
  overflow: hidden;
}

.quick-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
  transition: left 0.5s ease;
}

.quick-link:hover::before {
  left: 100%;
}

.quick-link:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
  border-color: rgba(102, 126, 234, 0.3);
  color: #667eea;
  transform: translateX(-4px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.dashboard-container:deep(.dark-mode) .quick-link {
  background: rgba(102, 126, 234, 0.1);
  border-color: rgba(102, 126, 234, 0.2);
  color: #e0e7ff;
}

.dashboard-container:deep(.dark-mode) .quick-link:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
  color: #ffffff;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .quick-access-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    padding: 20px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .quick-links {
    grid-template-columns: 1fr;
  }
}
</style>
